import React, {PropsWithChildren} from 'react';
import classNames from 'classnames';
import {
    CustomResizableLayout,
    ResizablePanel
} from '../../../../components/layout/resizable-layout';
import styles from '../query-layout.module.css';
import {isEditableQueryDraft} from '../../utils';
import {LocalErrorBoundary} from '../../../../components/error-boundary';
import {SelectedPathResultPanel} from './factset-panel/selected-path-result/selected-path-result-panel';
import {
    collapsedFactsetPanelHeight,
    QueryGraphFactsetPanel
} from './factset-panel/query-graph-factset-panel/query-graph-factset-panel';
import {usePathsResultListProps} from './right-panel/paths-result-list/use-paths-result-list-props';
import {useQueryLayoutProps} from '../../hooks/use-query-layout-props';
import {RightPanelNavBar} from './right-panel/right-panel-nav-bar';

interface Props {
    selectedQuery: CompletedQuery;
    resultsListProps: ReturnType<typeof usePathsResultListProps>;
    queryLayoutProps: ReturnType<typeof useQueryLayoutProps>;
    clearNodeSelection: () => void;
    clearResultListItemSelection: () => void;
    selectedNodes: number[];
    maxPathLength: number;
    canFetchNodeStructure: boolean;
}

export function CompletedQueryLayout({
    selectedQuery,
    resultsListProps,
    queryLayoutProps,
    selectedNodes,
    maxPathLength,
    clearNodeSelection,
    clearResultListItemSelection,
    children,
    canFetchNodeStructure
}: PropsWithChildren<Props>) {
    const hasResultsPanel =
        Boolean(resultsListProps.selectedResultItem) || selectedNodes.length > 0;

    const factsetPanelFixedSize = (function () {
        if (!hasResultsPanel) {
            return {panelIndex: 1 as const, size: 0};
        }

        if (queryLayoutProps.isFactsetCollapsed) {
            return {panelIndex: 1 as const, size: collapsedFactsetPanelHeight};
        }

        return undefined;
    })();

    return (
        <React.Fragment>
            <CustomResizableLayout
                localStorageKey="queries.results.chain.layoutSize"
                settings={{initialFirstPanelShare: 0.6, minPermittedHeight: 200}}
                fixedPanelSize={factsetPanelFixedSize}
                alignment="vertical"
            >
                {(resizableLayoutProps) => (
                    <React.Fragment>
                        <ResizablePanel
                            index={0}
                            {...resizableLayoutProps}
                            className={classNames(styles.queryLayout, {
                                [styles.viewMode]: !isEditableQueryDraft(selectedQuery)
                            })}
                        >
                            {children}
                        </ResizablePanel>

                        {!!resultsListProps.selectedResultItem && (
                            <LocalErrorBoundary variant="empty">
                                <ResizablePanel
                                    className={styles.factsetPanelBottom}
                                    index={1}
                                    {...resizableLayoutProps}
                                >
                                    <SelectedPathResultPanel
                                        queryInfo={selectedQuery}
                                        connectionId={resultsListProps.selectedResultItem}
                                        closePanel={clearResultListItemSelection}
                                        selectedConnectionIndex={
                                            resultsListProps.selectedConnectionIndex!
                                        }
                                        isPanelCollapsed={queryLayoutProps.isFactsetCollapsed}
                                        toggleIsPanelCollapsed={
                                            queryLayoutProps.toggleIsFactsetCollapsed
                                        }
                                    />
                                </ResizablePanel>
                            </LocalErrorBoundary>
                        )}

                        {selectedNodes.length > 0 && (
                            <LocalErrorBoundary variant="empty">
                                <ResizablePanel
                                    className={styles.factsetPanelBottom}
                                    index={1}
                                    {...resizableLayoutProps}
                                >
                                    <QueryGraphFactsetPanel
                                        queryInfo={selectedQuery}
                                        selectedNodes={selectedNodes}
                                        clearNodeSelection={clearNodeSelection}
                                        isPanelCollapsed={queryLayoutProps.isFactsetCollapsed}
                                        toggleIsPanelCollapsed={
                                            queryLayoutProps.toggleIsFactsetCollapsed
                                        }
                                        maxPathLength={maxPathLength}
                                        canFetchNodeStructure={canFetchNodeStructure}
                                    />
                                </ResizablePanel>
                            </LocalErrorBoundary>
                        )}
                    </React.Fragment>
                )}
            </CustomResizableLayout>

            <RightPanelNavBar queryLayoutProps={queryLayoutProps} />
        </React.Fragment>
    );
}
